import type { ComplexQuestionOption } from "@repo/app-types/entities/question";
import { reorderArrayItems } from "@repo/app-types/helpers/utilities";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { TooltipHoverTarget } from "@repo/ui/tooltip/hoverTarget";
import { SimpleTooltip } from "@repo/ui/tooltip/simpleTooltip";
import { TooltipTrigger } from "@repo/ui/tooltip/tooltipTrigger";
import type { FC } from "react";
import { SurveyOptionEditor } from "./surveyOptionEditor.js";

const DEFAULT_MAX_SURVEY_OPTIONS = 10;

interface SurveyOptionsEditorProps {
  options: ComplexQuestionOption[];
  onOptionsChange: (options: ComplexQuestionOption[]) => void;
  maxNumberOfOptions?: number;
}

export const SurveyOptionsEditor: FC<SurveyOptionsEditorProps> = ({
  options,
  onOptionsChange,
  maxNumberOfOptions,
}) => {
  const maxNumberOfSurveyOptions =
    maxNumberOfOptions || DEFAULT_MAX_SURVEY_OPTIONS;
  const isAtOptionLimit = options.length >= maxNumberOfSurveyOptions;

  const addOptionButton = (
    <Button
      variant={ButtonVariant.Secondary}
      isDisabled={isAtOptionLimit}
      onClick={() => {
        onOptionsChange([...options, { name: "" }]);
      }}
    >
      Add option
    </Button>
  );

  return (
    <>
      <div className="my-2 flex flex-col space-y-2">
        {options.map((option, index) => {
          const isDuplicate = options.some(
            (o, i) => i !== index && o.name && o.name === option.name,
          );

          return (
            <div key={`surveyoption-${index}`}>
              <SurveyOptionEditor
                value={option.name}
                onChange={(newValue: string) => {
                  onOptionsChange(
                    options.map((o, i) => {
                      return i === index ? { name: newValue } : o;
                    }),
                  );
                }}
                onRemove={() => {
                  onOptionsChange(options.filter((_, i) => i !== index));
                }}
                onClickReorder={(direction: "up" | "down") => {
                  const newOptions = reorderArrayItems(
                    options,
                    index,
                    direction,
                  );
                  if (newOptions) onOptionsChange(newOptions);
                }}
                isDuplicate={isDuplicate}
              />
            </div>
          );
        })}
      </div>
      <div className="mt-2 flex">
        {isAtOptionLimit ? (
          // A disabled button fires no hover events, so the tooltip hangs off a wrapper.
          <TooltipTrigger>
            <TooltipHoverTarget shape="rounded">
              {addOptionButton}
            </TooltipHoverTarget>
            <SimpleTooltip>
              We support at most {maxNumberOfSurveyOptions} survey options
              <br />
              to ensure a high-quality interview experience
            </SimpleTooltip>
          </TooltipTrigger>
        ) : (
          addOptionButton
        )}
      </div>
    </>
  );
};
